import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { HierarchyPointNode } from 'd3';
import { Subject } from 'rxjs';
import { DThreeNodeEnterElement } from 'src/app/shared/models/d-three-types';
import { TrainingStatus } from 'src/app/shared/models/training-status';
import { ApplicationLockService } from '../../shared/services/application-lock.service';
import { Check } from '../models/training-prestart-check';
import { TrainingTreeNode } from '../models/training-tree-node';
import { JobService } from './job.service';
import { TreeStateService } from './tree-state.service';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogData,
} from '../../shared/components/confirmation-modal/confirmation-dialog.component';
import { MAT_DIALOG_CONFIG_DATA_INSUFFICIENT_CREDIT_AMOUNT_ERROR } from 'src/app/shared/shared-error-dialog-data';
import { ChecksModalComponent } from 'src/app/shared/components/checks-modal/checks-modal.component';

const MAT_DIALOG_CONFIG_DATA_SUBSCRIPTION_ERROR: ConfirmationDialogData = {
    headerTranslationKey:
        'splitScreen.components.subscriptionErrorDialog.header',
    confirmationQuestion: {
        translationKey:
            'splitScreen.components.subscriptionErrorDialog.confirmationQuestion',
        interpolateParameters: null,
    },
    confirmButtonTranslationKey: null,
    rejectButtonTranslationKey:
        'splitScreen.components.subscriptionErrorDialog.confirm',
    transferData: false,
};

@Injectable()
export class TreeActionsService {
    readonly APP_LOCK_MESSAGE: string = 'appLock.trainingPreStartCheck';

    /**
     * Notifies the <code>SplitScreenTabsComponent</code> that training has started,
     * allowing it to persist data before the API request is sent.
     *
     * The emitted value is the `nodeId`, which must later be emitted back via {@link $canStart}.
     */
    $startTriggered: Subject<string> = new Subject<string>();

    /**
     * Emits when a node can be started.
     * Use an object like: `{ nodeId: string; error?: Error }`.
     * - `nodeId`: ID of the node to start
     * - `error` (optional): Error object if an exception occurred during persisting
     */
    $canStart: Subject<{ nodeId: string; error?: Error }> = new Subject();

    private treeNodes: Map<string, TrainingTreeNode> = new Map();

    private matDialogConfig: MatDialogConfig<ConfirmationDialogData>;

    constructor(
        private treeStateService: TreeStateService,
        private jobService: JobService,
        private translateService: TranslateService,
        private lockService: ApplicationLockService,
        private matDialog: MatDialog
    ) {
        this.$canStart.subscribe((value: { nodeId: string; error?: Error }) => {
            if (value.error) {
                this.refreshTreeState(value.nodeId);
            } else {
                this.startJob(value.nodeId);
            }
        });
        this.matDialogConfig = new MatDialogConfig();
    }

    addNodeClickAction(enterElement: DThreeNodeEnterElement) {
        enterElement
            .selectAll('foreignObject')
            .on(
                'mouseup',
                (event$, node: HierarchyPointNode<TrainingTreeNode>) =>
                    this.handleClick(event$, node)
            );
    }

    addNodeDoubleClickAction(enterElement: DThreeNodeEnterElement) {
        enterElement
            .selectAll('foreignObject')
            .on(
                'dblclick',
                (event$, node: HierarchyPointNode<TrainingTreeNode>) => {
                    this.handleDoubleClick(event$, node);
                }
            );
    }

    handleClick(
        event$: MouseEvent,
        node: HierarchyPointNode<TrainingTreeNode>
    ) {
        if (this.treeStateService.isSelectedTrainingNameValid()) {
            const elementId = event$.target['id'];
            const treeNode = node.data;

            if (this.treeStateService.selectedTreeNode.uuid !== treeNode.uuid) {
                this.treeStateService.isMenuOpen =
                    elementId === 'training-node-menu';
                this.treeStateService.selectedTreeNode = treeNode;
            } else
                switch (elementId) {
                    case 'training-node-menu':
                        this.toggleMenuAndUpdateTree(treeNode);
                        break;
                    case 'training-node-start':
                        this.start(treeNode);
                        this.toggleMenuAndUpdateTree(treeNode);
                        break;
                    case 'training-node-stop':
                        this.stop(treeNode);
                        this.toggleMenuAndUpdateTree(treeNode);
                        break;
                    case 'training-node-delete':
                        this.openDeletionConfirmDialog(treeNode);
                        this.toggleMenuAndUpdateTree(treeNode);
                        break;
                    case 'training-node-clone':
                        this.clone(treeNode);
                        this.toggleMenuAndUpdateTree(treeNode);
                        break;
                    case 'training-node-download':
                        this.treeStateService.downloadTrainingSkill(
                            treeNode.uuid
                        );
                        this.toggleMenuAndUpdateTree(treeNode);
                        break;
                }
        }
    }

    handleDoubleClick(
        event$: MouseEvent,
        node: HierarchyPointNode<TrainingTreeNode>
    ) {
        if (this.treeStateService.isSelectedTrainingNameValid()) {
            const elementId = event$.target['id'];
            const treeNode: TrainingTreeNode = node.data;

            if (elementId === 'training-node-name') {
                this.treeStateService.prepareTrainingNameEditing(treeNode.name);
                this.treeStateService.selectedTreeNode = treeNode;
            }
        }
    }

    private toggleMenuAndUpdateTree(treeNode: TrainingTreeNode) {
        this.treeStateService.isMenuOpen = !this.treeStateService.isMenuOpen;
        this.treeStateService.selectedTreeNode = treeNode;
    }

    /**
     * Sets the status of the provided node to <code>QUEUED</code>, stops tree updates, and emits
     * {@link $startTriggered} to notify the <code>SplitScreenTabsComponent</code> about the training start.
     *
     * The actual training starts in {@link TreeActionsService.startJob}, which is triggered
     * after {@link $canStart} emits a new value.
     *
     * @param treeNode The training tree node to be started.
     */
    start(treeNode: TrainingTreeNode): void {
        this.lockService.setApplicationLock(this.APP_LOCK_MESSAGE);

        treeNode.status = TrainingStatus.QUEUED;
        this.treeNodes.set(treeNode.uuid, treeNode);

        this.treeStateService.startedTrainings.push(treeNode.uuid);
        this.treeStateService.setTreeHasRunningTraining(false);
        this.$startTriggered.next(treeNode.uuid);
    }

    private startJob(nodeId: string, ignoreGradeHint: boolean = false) {
        this.jobService
            .start(this.treeNodes.get(nodeId), ignoreGradeHint)
            .subscribe({
                next: () => this.refreshTreeState(nodeId),
                error: (error) => {
                    if (error?.error?.checks) {
                        this.openChecksModal(error.error.checks, nodeId);
                    } else if (error?.error?.message === 'SUBSCRIPTION_ERROR') {
                        this.openErrorDialog(
                            nodeId,
                            MAT_DIALOG_CONFIG_DATA_SUBSCRIPTION_ERROR
                        );
                    } else if (
                        error?.error?.type ===
                        'InsufficientCreditAmountException'
                    ) {
                        this.openErrorDialog(
                            nodeId,
                            MAT_DIALOG_CONFIG_DATA_INSUFFICIENT_CREDIT_AMOUNT_ERROR
                        );
                    } else {
                        this.refreshTreeState(nodeId);
                    }
                },
            });
    }

    private openChecksModal(checks: Check[], nodeId: string) {
        this.lockService.releaseApplicationLock();

        const config = new MatDialogConfig();
        config.data = {
            checks,
            internationalizationPrefix:
                'splitScreen.components.trainingChecksModal',
        };
        config.autoFocus = false;

        this.matDialog
            .open(ChecksModalComponent, config)
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.startJob(nodeId, true);
                } else {
                    this.refreshTreeState(nodeId);
                }
            });
    }

    private openErrorDialog(
        nodeId: string,
        dialogData: ConfirmationDialogData
    ) {
        this.lockService.releaseApplicationLock();
        this.matDialogConfig.data = dialogData;
        this.matDialog
            .open(ConfirmationDialogComponent, this.matDialogConfig)
            .afterClosed()
            .subscribe(() => {
                this.refreshTreeState(nodeId);
            });
    }

    refreshTreeState(nodeId: string) {
        this.treeStateService.startedTrainings =
            this.treeStateService.startedTrainings.filter(
                (startedTrainingId: string) => startedTrainingId !== nodeId
            );

        if (this.treeStateService.startedTrainings.length === 0) {
            this.treeStateService.refreshTreeState().subscribe();
        }

        this.treeNodes.delete(nodeId);
        this.lockService.releaseApplicationLock();
    }

    stop(treeNode: TrainingTreeNode): void {
        this.jobService.stop(treeNode).subscribe(() => {
            treeNode.status = TrainingStatus.CANCELLING;
        });
    }

    clone(treeNode: TrainingTreeNode): void {
        this.treeStateService.cloneTreeNode(
            treeNode.uuid,
            this.translateService.instant(
                'splitScreen.components.contextMenu.cloneSuffix'
            )
        );
    }

    openDeletionConfirmDialog(treeNode: TrainingTreeNode): void {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.width = '550px';
        dialogConfig.data = {
            headerTranslationKey:
                'splitScreen.components.contextMenu.deleteDialogHeader',
            confirmationQuestion: {
                translationKey: 'splitScreen.components.contextMenu.delete2',
                interpolateParameters: { name: treeNode.name },
            },
            confirmButtonTranslationKey:
                'splitScreen.components.contextMenu.delete1',
            rejectButtonTranslationKey:
                'splitScreen.components.contextMenu.keep',
            transferData: true,
        } as ConfirmationDialogData;

        this.matDialog
            .open(ConfirmationDialogComponent, dialogConfig)
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.deleteNode(treeNode.uuid);
                }
            });
    }

    deleteNode(trainingId: string): void {
        this.treeStateService.deleteTreeNode(trainingId);
    }
}
